import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import AgentLayout from './AgentLayout';

function AgentSettings() {
  const [profile, setProfile] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [profileErrors, setProfileErrors] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);

  const [pwForm, setPwForm] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });
  const [pwErrors, setPwErrors] = useState({});
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.get('/agent/profile');
      setProfile({
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
      });
    } catch (err) {
      console.error('Failed to load profile', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleProfileChange = (e) =>
    setProfile({ ...profile, [e.target.name]: e.target.value });

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileErrors({});
    setProfileSuccess(false);
    try {
      await api.put('/agent/profile', {
        name: profile.name,
        phone: profile.phone,
      });
      setProfileSuccess(true);
    } catch (err) {
      const data = err.response?.data;
      setProfileErrors(data?.errors || { general: [data?.message || 'Something went wrong'] });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePwChange = (e) =>
    setPwForm({ ...pwForm, [e.target.name]: e.target.value });

  const handlePwSubmit = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    setPwErrors({});
    setPwSuccess(false);
    try {
      await api.put('/agent/change-password', pwForm);
      setPwSuccess(true);
      setPwForm({ current_password: '', new_password: '', new_password_confirmation: '' });
    } catch (err) {
      const data = err.response?.data;
      setPwErrors(data?.errors || { general: [data?.message || 'Something went wrong'] });
    } finally {
      setPwSaving(false);
    }
  };


  return (
    <AgentLayout>
      <div className="bg-[#FDF5FF] -m-6 p-6 min-h-screen">
      <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mb-1">Settings</h2>
      <p className="text-gray-500 text-sm mb-6">Manage your profile and account security.</p>

      {loading && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center text-gray-400">
          Loading…
        </div>
      )}

      {!loading && loadError && (
       <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center text-red-500">
          Could not load your profile.{' '}
           <button onClick={loadProfile} className="underline text-purple-600">Retry</button>
        </div>
      )}

      {!loading && !loadError && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Profile section */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-bold text-lg mb-4 bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Profile Details</h3>

            {profileSuccess && (
              <div className="mb-4 bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg">
                Profile updated successfully.
              </div>
            )}
            {profileErrors.general && (
              <div className="mb-4 bg-red-50 text-red-700 text-sm px-4 py-2 rounded-lg">
                {profileErrors.general[0]}
              </div>
            )}

            <form onSubmit={handleProfileSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={profile.name}
                  onChange={handleProfileChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D]"
                />
                {profileErrors.name && (
                  <p className="text-red-500 text-xs mt-1">{profileErrors.name[0]}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className="w-full border border-gray-200 bg-gray-50 text-gray-400 rounded-xl px-3 py-2 text-sm cursor-not-allowed"
                />
                <p className="text-xs text-gray-400 mt-1">Contact an admin to change your email.</p>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (10 digit)</label>
                <input
                  type="text"
                  name="phone"
                  value={profile.phone}
                  onChange={handleProfileChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D]"
                />
                {profileErrors.phone && (
                  <p className="text-red-500 text-xs mt-1">{profileErrors.phone[0]}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={profileSaving}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {profileSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>

          {/* Change password section */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-bold text-lg mb-4 bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Change Password</h3>

            {pwSuccess && (
              <div className="mb-4 bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg">
                Password changed successfully.
              </div>
            )}
            {pwErrors.general && (
              <div className="mb-4 bg-red-50 text-red-700 text-sm px-4 py-2 rounded-lg">
                {pwErrors.general[0]}
              </div>
            )}

            <form onSubmit={handlePwSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input
                  type="password"
                  name="current_password"
                  value={pwForm.current_password}
                  onChange={handlePwChange}
                   className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                />
                {pwErrors.current_password && (
                  <p className="text-red-500 text-xs mt-1">{pwErrors.current_password[0]}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  name="new_password"
                  value={pwForm.new_password}
                  onChange={handlePwChange}
                  minLength={8}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D]"
                />
                {pwErrors.new_password && (
                  <p className="text-red-500 text-xs mt-1">{pwErrors.new_password[0]}</p>
                )}
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  name="new_password_confirmation"
                  value={pwForm.new_password_confirmation}
                  onChange={handlePwChange}
                  minLength={8}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D]"
                />
              </div>

              <button
                type="submit"
                disabled={pwSaving}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {pwSaving ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}
      </div>
    </AgentLayout>
  );
}

export default AgentSettings;